/**
 * useFilters Hook
 *
 * CRUD for email filters and test filter via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailFilter, EmailCreateFilterInput, EmailUpdateFilterInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useFilters(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.filters(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailFilters(accountId);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    if (accountId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.email.filters(accountId),
      });
    }
  }, [accountId, queryClient]);

  const createFilter = useCallback(
    async (input: EmailCreateFilterInput) => {
      const result = await api.createEmailFilter(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateFilter = useCallback(
    async (filterId: string, input: EmailUpdateFilterInput) => {
      if (!accountId) return;
      const result = await api.updateEmailFilter(filterId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteFilter = useCallback(
    async (filterId: string) => {
      if (!accountId) return;
      await api.deleteEmailFilter(filterId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  const testFilter = useCallback(
    async (filterId: string) => {
      if (!accountId) return { matchingMessageIds: [] as string[], totalTested: 0 };
      return api.testEmailFilter(filterId, accountId);
    },
    [accountId],
  );

  const applyAllFilters = useCallback(async () => {
    if (!accountId) return;
    return api.applyEmailFilters(accountId);
  }, [accountId]);

  return {
    filters: queryResult.data?.filters ?? ([] as EmailFilter[]),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createFilter,
    updateFilter,
    deleteFilter,
    testFilter,
    applyAllFilters,
  };
}
